import {
  X_MAX_TWEET_LENGTH,
  countTweetLength,
  type MediaAttachment,
  type PostWithTargets,
} from '@postpilot/core/domain';
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/input';
import { api, ApiError } from '@/lib/api';
import { useAccountsStore } from '@/stores/accounts-store';
import { usePostsStore } from '@/stores/posts-store';
import { AccountSelector } from '../composer/account-selector';
import { CharacterRing } from '../composer/character-ring';
import { MediaUploader } from '../composer/media-uploader';
import { SchedulePicker } from '../composer/schedule-picker';

/**
 * Editing a queued post.
 *
 * Reuses the composer's parts rather than reimplementing them — the character
 * ring, the account chips and the schedule picker all behave identically here,
 * which is the point: editing a post should feel like writing one.
 */
export function EditPostDialog({
  post,
  onClose,
}: {
  post: PostWithTargets | null;
  onClose: () => void;
}) {
  const accounts = useAccountsStore((s) => s.accounts);
  const connect = useAccountsStore((s) => s.connect);
  const refetchQueue = usePostsStore((s) => s.fetch);

  const [content, setContent] = React.useState('');
  const [media, setMedia] = React.useState<MediaAttachment[]>([]);
  const [accountIds, setAccountIds] = React.useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // Re-seed the form whenever a different post is opened. Keying off `post.id`
  // rather than `post` avoids resetting the user's edits when a realtime update
  // refreshes the object identity mid-edit.
  React.useEffect(() => {
    if (!post) return;

    setContent(post.content);
    setMedia(post.media);
    // Targets the user cancelled are not "selected" — only live ones are.
    setAccountIds(post.targets.filter((t) => t.status !== 'cancelled').map((t) => t.accountId));
    setScheduledAt(post.scheduledAt);
    setErrors({});
  }, [post?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const charCount = countTweetLength(content);
  const isOverLimit = charCount > X_MAX_TWEET_LENGTH;

  const save = async () => {
    if (!post) return;

    setSaving(true);
    setErrors({});

    try {
      await api.posts.update(post.id, {
        content,
        media,
        accountIds,
        scheduledAt,
      });

      toast.success('Post updated');
      await refetchQueue();
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.fields) setErrors(error.fields);
        toast.error('Could not save', { description: error.message });
      } else {
        toast.error('Something went wrong');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={post !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit post</DialogTitle>
          <DialogDescription>
            Changes apply to accounts this post has not gone out to yet. Anything already published
            on X stays as it is.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <AccountSelector
            accounts={accounts}
            selectedIds={accountIds}
            onToggle={(id) =>
              setAccountIds((current) =>
                current.includes(id) ? current.filter((a) => a !== id) : [...current, id],
              )
            }
            onSelectAll={() =>
              setAccountIds(accounts.filter((a) => a.status === 'active').map((a) => a.id))
            }
            onClear={() => setAccountIds([])}
            onConnect={() => void connect('/queue')}
            error={errors.accountIds}
          />

          <div className="relative">
            <Textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              rows={6}
              aria-invalid={isOverLimit}
              aria-label="Post content"
              className="resize-none pb-10"
            />
            <div className="absolute bottom-2.5 right-3">
              <CharacterRing count={charCount} />
            </div>
          </div>
          {errors.content && (
            <p className="text-destructive -mt-3 text-xs" role="alert">
              {errors.content}
            </p>
          )}

          <MediaUploader media={media} onChange={setMedia} />

          <SchedulePicker
            value={scheduledAt}
            onChange={setScheduledAt}
            error={errors.scheduledAt}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void save()}
            loading={saving}
            disabled={isOverLimit || accountIds.length === 0}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
